import constants from 'app/constants'

/**
 * Filter out the any base categories by checking the current item layer
 *
 * @param {object} props - the accessory props
 * @param {string} props.layer - the accessory layer (category)
 * @returns {boolean} whether it was filtered
 */
const filterOutBaseCategories = ({ layer }) =>
  !constants.AVATAR_BASE_LAYERS.includes(layer)

/**
 * Merge matching accessories together
 *
 * @param {Array} accessories - the accessories list
 * @returns {Array} the merged accessories
 */
const mergeAccessories = accessories => {
  const merged = new Map()

  accessories.forEach(item => {
    if (merged.has(item.id)) {
      merged.get(item.id).userOwnedCount++

      merged.get(item.id).attachedTo = [
        ...merged.get(item.id).attachedTo,
        ...item.attachedTo
      ]
    } else {
      merged.set(item.id, { ...item, userOwnedCount: 1 })
    }
  })

  return Array.from(merged.values())
}

/**
 * Parse user accessories into 3 key buckets;
 * attached, detached & all (attached + detached)
 *
 * @param {object} props - the input props
 * @returns {object} the parsed user accessories
 */
export default function parseUserAccessories (props) {
  const { accessories, avatars, categoryConfig, elementsKeys } = props

  let attachedAccessories = avatars
    .sort((a, b) => b.id - a.id)
    .flatMap(avatar =>
      avatar.items
        .map(elementId => elementsKeys[elementId])
        .filter(match => match !== undefined)
        .map(match => ({
          ...match,
          type: match.type ? match.type.toLowerCase() : null,
          gender: match.gender ? match.gender : [],
          attachedTo: [avatar.id],
          url: `/account/wardrobe/${match.id}`,
          layerLabel: categoryConfig?.[match?.layer]?.label || 'Base'
        }))
    )
    .filter(filterOutBaseCategories)

  let detachedAccessories = accessories
    .map(elementId => {
      const match = elementsKeys[elementId]

      return match !== undefined
        ? {
            ...match,
            type: match.type ? match.type.toLowerCase() : null,
            gender: match.gender ? match.gender : [],
            attachedTo: [-1],
            url: `/account/wardrobe/${match.id}`,
            layerLabel: categoryConfig?.[match?.layer]?.label || 'Base'
          }
        : null
    })
    .filter(match => match !== null)
    .filter(filterOutBaseCategories)
    .reverse() // Reverse to put in order of latest

  // Get the total count before we merge them together (to combine duplicate accessories)
  const countAllAccessories =
    attachedAccessories.length + detachedAccessories.length

  const allAccessories = mergeAccessories([
    ...attachedAccessories,
    ...detachedAccessories
  ])

  attachedAccessories = mergeAccessories(attachedAccessories)
  detachedAccessories = mergeAccessories(detachedAccessories)

  return {
    countAllAccessories,
    attachedAccessories,
    detachedAccessories,
    allAccessories
  }
}
